import { useEffect, useState } from "react";

import { isRealWebview } from "./isWebview";

/**
 * Bridge contract between the mobile WebView and the BTNet web app for
 * propagating the user's JobPicker selection. Replaces the legacy
 * `?jobIds=1,2,3,...` URL query string, which WAFs reject when users have
 * very large selections.
 *
 * In production, the mobile WebView injects `window.initialBtMobileContext`
 * before content load and dispatches `CustomEvent("btMobileContextChange")`
 * for reactive updates. In dev with `VITE_MOCK_WEBVIEW=true`, the BTNet
 * bootstrap calls `initializeMockBtMobileContext` to fake the same surface
 * from the desktop JobPicker's persisted state.
 *
 * The contract type and the underlying `Window` augmentation stay private to
 * this module — consumers read and write through the exported helpers
 * (`getInitialBtMobileContext`, `initializeMockBtMobileContext`,
 * `useBtMobileContext`) so the bridge shape doesn't leak into the global
 * namespace of every package consumer.
 */
interface IBtMobileContext {
  selectedJobIds: number[];
}

const BT_MOBILE_CONTEXT_CHANGE_EVENT = "btMobileContextChange";

interface IBtMobileContextWindow {
  initialBtMobileContext?: IBtMobileContext;
}

function getBtMobileContextWindow(): IBtMobileContextWindow {
  return window as Window & IBtMobileContextWindow;
}

function isValidContext(
  candidate: IBtMobileContext | null | undefined,
): candidate is IBtMobileContext {
  return Array.isArray(candidate?.selectedJobIds);
}

/**
 * Reads the one-shot mobile-context global injected by the mobile WebView at
 * page boot (or by the dev-mode bootstrap when `VITE_MOCK_WEBVIEW=true`).
 * Returns `undefined` when the global is absent — callers should treat that
 * as "no mobile-injected selection, fall back to URL / session storage".
 */
export function getInitialBtMobileContext(): IBtMobileContext | undefined {
  return getBtMobileContextWindow().initialBtMobileContext;
}

/**
 * Initializes the one-shot mobile-context global from a synthetic dev/mock
 * source (the desktop JobPicker, when `VITE_MOCK_WEBVIEW=true`). Faking the
 * same surface that the real mobile WebView injects via
 * `injectedJavaScriptBeforeContentLoaded` in production.
 *
 * No-op when running inside a real mobile WebView at boot — the production
 * injection always wins, so can enable the `VITE_MOCK_WEBVIEW` flag for the
 * browser and still test in the mobile app locally.
 *
 * Callers should still gate this on
 * `import.meta.env.DEV && import.meta.env.VITE_MOCK_WEBVIEW === "true"` —
 * keeping the env check at the call site lets Vite tree-shake the entire
 * dev-only path (including any storage reads needed to compute `context`)
 * out of production builds.
 */
export function initializeMockBtMobileContext(context: IBtMobileContext): void {
  if (isRealWebview) return;
  getBtMobileContextWindow().initialBtMobileContext = context;
}

/**
 * Returns the latest mobile-picker context for the lifetime of the calling
 * component: seeded from the one-shot global on mount, then updated as
 * `CustomEvent("btMobileContextChange")` events fire on `window`.
 *
 * Malformed payloads (where `selectedJobIds` is not an array) are ignored —
 * the previous valid value is preserved. BTNet enforces (via ESLint) that
 * the `useJobIdState` hook is the only consumer in the web app, so downstream
 * state never drifts from the canonical mobile selection.
 */
export function useBtMobileContext(): IBtMobileContext | undefined {
  const [context, setContext] = useState<IBtMobileContext | undefined>(() => {
    const initial = getInitialBtMobileContext();
    return isValidContext(initial) ? initial : undefined;
  });

  useEffect(() => {
    const listener = (event: Event) => {
      const next = (event as CustomEvent<IBtMobileContext | null | undefined>)
        .detail;
      if (isValidContext(next)) {
        setContext(next);
      }
    };
    window.addEventListener(BT_MOBILE_CONTEXT_CHANGE_EVENT, listener);
    return () => {
      window.removeEventListener(BT_MOBILE_CONTEXT_CHANGE_EVENT, listener);
    };
  }, []);

  return context;
}
